import { module } from "angular";
import { map } from "rxjs/operators";

import type { Observable } from "rxjs";

import type { IHttpService } from "App/Modules/Services";

import type { IEquipmentFunctionalityType, IEquipmentFunctionalityTypeSlim, IResponse } from "App/Modules/Models";
import { EquipmentFunctionalityClass, EquipmentFunctionalityTypeFunctions } from "App/Modules/Models";

import { cloneDeep, forEach, map as _map } from "lodash";
import { DirectConnectionType } from "App/Modules/Models/Models/DirectConnectionType";
import type { IAvailableChannel } from "App/Modules/Models/Models/IAvailableChannel";
import type { IEquipmentFunctionalityTypesRepository } from "./IEquipmentFunctionalityTypesRepository";

export default class EquipmentFunctionalityTypesRepository implements IEquipmentFunctionalityTypesRepository {
    public static $inject: string[] = ["mmHttpService", "$translate"];

    constructor(
        private readonly httpService: IHttpService,
        private readonly $translate: angular.translate.ITranslateService,
    ) {}

    public getFunctionalityType(functionalityTypeId: string): Observable<IResponse<IEquipmentFunctionalityType>> {
        return this.httpService
            .get<IEquipmentFunctionalityType>(`equipment/types/functionalities/${functionalityTypeId}`)
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityType>) =>
                    this.enhanceFunctionalityTypeResponse(response),
                ),
            );
    }

    public getFunctionalityTypes(): Observable<IResponse<IEquipmentFunctionalityTypeSlim[]>> {
        return this.httpService
            .get<IEquipmentFunctionalityTypeSlim[]>("equipment/types/functionalities")
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityTypeSlim[]>) =>
                    this.enhanceFunctionalityTypeSlimsResponse(response),
                ),
            );
    }

    public search(search: string): Observable<IResponse<IEquipmentFunctionalityTypeSlim[]>> {
        return this.httpService
            .get<IEquipmentFunctionalityTypeSlim[]>(`equipment/types/functionalities?$search=${search}`)
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityTypeSlim[]>) =>
                    this.enhanceFunctionalityTypeSlimsResponse(response),
                ),
            );
    }

    public addFunctionalityType(
        functionalityType: IEquipmentFunctionalityTypeSlim,
    ): Observable<IResponse<IEquipmentFunctionalityTypeSlim>> {
        return this.httpService
            .post<IEquipmentFunctionalityTypeSlim>("equipment/types/functionalities", functionalityType)
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityTypeSlim>) =>
                    this.enhanceFunctionalityTypeSlimResponse(response),
                ),
            );
    }

    public patchEquipmentFunctionalityType(
        equipmentFunctionalityTypeId: string,
        equipmentFunctionalityType: IEquipmentFunctionalityType,
    ): Observable<IResponse<IEquipmentFunctionalityType>> {
        const patchObject = cloneDeep(equipmentFunctionalityType);

        if (patchObject.class) {
            patchObject.class = EquipmentFunctionalityClass[equipmentFunctionalityType.class] as any;
        }

        forEach(patchObject.availableChannels, (channel: IAvailableChannel) => {
            channel.supportedDirectConnections = _map(
                channel.supportedDirectConnections,
                (connection) => DirectConnectionType[connection] as any,
            );
        });

        return this.httpService
            .patch<IEquipmentFunctionalityType>(
                `equipment/types/functionalities/${equipmentFunctionalityTypeId}`,
                patchObject,
            )
            .pipe(
                map((response: IResponse<IEquipmentFunctionalityType>) =>
                    this.enhanceFunctionalityTypeResponse(response),
                ),
            );
    }

    public deleteEquipmentFunctionalityType(functionalityTypeId: string): Observable<IResponse<void>> {
        return this.httpService.delete(`equipment/types/functionalities/${functionalityTypeId}`);
    }

    public getFpses(): Observable<IResponse<number[]>> {
        return this.httpService.get<number[]>("equipment/types/functionalities/fps");
    }

    public getResolutions(): Observable<IResponse<string[]>> {
        return this.httpService.get<string[]>("equipment/types/functionalities/resolutions");
    }

    private enhanceFunctionalityTypeSlimsResponse(
        response: IResponse<IEquipmentFunctionalityTypeSlim[]>,
    ): IResponse<IEquipmentFunctionalityTypeSlim[]> {
        response.data = response.data.map((ft: IEquipmentFunctionalityTypeSlim) =>
            EquipmentFunctionalityTypeFunctions.enhanceFunctionalityTypeSlim(ft),
        );
        return response;
    }

    private enhanceFunctionalityTypeSlimResponse(
        response: IResponse<IEquipmentFunctionalityTypeSlim>,
    ): IResponse<IEquipmentFunctionalityTypeSlim> {
        response.data = EquipmentFunctionalityTypeFunctions.enhanceFunctionalityTypeSlim(response.data);
        return response;
    }

    private enhanceFunctionalityTypeResponse(
        response: IResponse<IEquipmentFunctionalityType>,
    ): IResponse<IEquipmentFunctionalityType> {
        response.data = EquipmentFunctionalityTypeFunctions.enhanceFunctionalityType(response.data);
        return response;
    }
}

module("mmRepositories").service("irEquipmentFunctionalityTypesRepository", EquipmentFunctionalityTypesRepository);
